/**
 * De que tamanho o desenho de combate de um monstro entra no tabuleiro.
 *
 * Vive fora do tabuleiro de propósito: é aritmética pura, sem Pixi, e assim
 * pode ser conferida sem subir um navegador inteiro.
 */

import { TILE_SIZE } from './tiles.js';

/*
 * Os battlers do acervo vão de 66×84 (um ovo com pernas) a 552×320 (um deus
 * demoníaco). Pôr todos no mesmo tamanho apaga a diferença que o desenho já
 * conta; pôr cada um no tamanho natural entrega um dragão de onze casas de
 * largura, que cobre metade do tabuleiro e esconde o terreno que decide a
 * jogada. A saída é comprimir a escala em vez de anulá-la.
 */
/** O battler de porte médio do acervo, contra o qual os outros são medidos. */
const MAP_ART_REFERENCE = 180;
/** E o tamanho com que ele entra: uma casa, como um personagem comum. */
const MAP_ART_BASE = TILE_SIZE;
/**
 * O quanto a diferença de tamanho sobrevive à compressão.
 *
 * Em 1 o dragão entra com sete vezes a altura do ovo e cobre o tabuleiro; em 0
 * todos entram iguais e o desenho para de dizer o que é grande. Em 0,45 um
 * battler quatro vezes maior aparece pouco menos de duas vezes maior — que é
 * a mesma licença que a série toma com cavalaria e dragões.
 */
const MAP_ART_COMPRESSION = 0.45;
const MAP_ART_MIN = 30;
/** Teto: um monstro grande ocupa a casa dele e um tanto, nunca a do vizinho. */
const MAP_ART_MAX = TILE_SIZE * 1.45;

export function mapArtScale(width: number, height: number): number {
  const natural = Math.max(width, height);
  if (!Number.isFinite(natural) || natural <= 0) return 1;
  const wanted = MAP_ART_BASE * (natural / MAP_ART_REFERENCE) ** MAP_ART_COMPRESSION;
  const target = Math.min(MAP_ART_MAX, Math.max(MAP_ART_MIN, wanted));
  return target / natural;
}

/**
 * A mesma proporção, dentro de um quadro de prévia.
 *
 * O painel mostra a arte numa caixa de lado fixo, e a pergunta ali é a mesma
 * do tabuleiro: quão grande este monstro é comparado aos outros. Ancorar pelo
 * teto — o maior possível preenche a caixa — preserva a comparação e garante
 * que nada seja recortado pela borda, que era o que acontecia ao ampliar pela
 * casa: um battler de casa e meia não cabe num quadro de uma casa.
 */
export function mapArtPreviewScale(width: number, height: number, box: number): number {
  return mapArtScale(width, height) * (box / MAP_ART_MAX);
}

/*
 * Quantas casas o monstro ocupa.
 *
 * A compressão acima resolve o problema de um dragão não cobrir meio tabuleiro,
 * mas ela cobra um preço: comprimido dentro de uma casa, o dragão fica do
 * tamanho de um goblin gordo, e o desenho para de dizer quem é grande. Os
 * maiores battlers do acervo passam então a ocupar a área que o desenho pede —
 * de duas a quatro casas — e é essa área que bloqueia passagem, é alcançada e
 * é cercada.
 *
 * Os limiares saem da distribuição real do acervo: dos 136 battlers de combate
 * lateral, cerca de um terço cruza a marca, e dois terços continuam cabendo
 * numa casa como sempre couberam.
 */
/** Abaixo disto, na maior das duas medidas, o battler continua numa casa só. */
const BIG_BODY_THRESHOLD = 240;
/** E, passada a marca, o eixo que chega aqui vale por duas casas. */
const BIG_BODY_AXIS = 200;

export interface Footprint {
  readonly w: number;
  readonly h: number;
}

const ONE_TILE: Footprint = { w: 1, h: 1 };

export function mapArtFootprint(width: number, height: number): Footprint {
  const natural = Math.max(width, height);
  if (!Number.isFinite(natural) || natural < BIG_BODY_THRESHOLD) return ONE_TILE;

  const w = width >= BIG_BODY_AXIS ? 2 : 1;
  const h = height >= BIG_BODY_AXIS ? 2 : 1;
  return w === 1 && h === 1 ? ONE_TILE : { w, h };
}

/**
 * De que tamanho o desenho entra numa área de mais de uma casa.
 *
 * Aqui não há compressão nenhuma a fazer: a área já foi escolhida pelo tamanho
 * do bicho, e o que resta é encaixá-lo nela. A folga vertical é de propósito —
 * uma criatura desenhada de perfil se apoia no chão da casa de baixo e pode
 * passar um pouco por cima da de cima, como a copa de uma árvore passa.
 */
const BODY_OVERHANG = 1.12;

export function mapArtBodyScale(
  width: number,
  height: number,
  footprint: Footprint,
  tileSize: number = TILE_SIZE,
): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return 1;
  if (footprint.w === 1 && footprint.h === 1) return mapArtScale(width, height);

  const boxWidth = footprint.w * tileSize;
  const boxHeight = footprint.h * tileSize * BODY_OVERHANG;
  return Math.min(boxWidth / width, boxHeight / height);
}
